import { useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts'
import type { AgentBuilderReportData } from './types'
import { chartTooltipProps, DONUT_COLORS } from './chartTheme'
import { findDrilldownColumn } from './drilldownHierarchy'
import { classifyColumns, deriveChartDataForDimension, formatColumnValue, formatDimensionLabel, formatFieldDisplayName, pickLabelColumn } from './vizUtils'

const PRINT_DONUT_WIDTH = 660
const PRINT_DONUT_HEIGHT = 260

export function DonutViz({
  data,
  printMode = false,
  onSelect,
}: {
  data: AgentBuilderReportData
  printMode?: boolean
  onSelect?: (field: string, value: string) => void
}) {
  const { dimensionCols, measureCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const activeMeasure = measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
  const availableDimensions = data.dimensions?.length ? data.dimensions : dimensionCols
  const defaultDimension = useMemo(
    () => data.groupByDimension ?? pickLabelColumn(dimensionCols, data.rows),
    [data.groupByDimension, dimensionCols, data.rows],
  )
  const [selectedDimension, setSelectedDimension] = useState<string | null>(defaultDimension)

  useEffect(() => {
    setSelectedDimension(defaultDimension)
  }, [defaultDimension])

  const chartData = useMemo(() => {
    if (!selectedDimension) return data.chartData
    // The dimension is a field ref ("'Jobs'[Job Category]"); rows are keyed by the returned column name.
    const column = findDrilldownColumn(data.columns, selectedDimension) ?? selectedDimension
    const derived = deriveChartDataForDimension(data.columns, data.rows, column)
    return derived.length > 0 ? derived : data.chartData
  }, [data.chartData, data.columns, data.rows, selectedDimension])

  if (chartData.length === 0) {
    return <p className="py-4 text-sm text-gray-400">No chart data available.</p>
  }

  const chart = (
    <PieChart width={printMode ? PRINT_DONUT_WIDTH : undefined} height={printMode ? PRINT_DONUT_HEIGHT : undefined}>
      <Pie
        data={chartData}
        dataKey="value"
        nameKey="label"
        innerRadius={printMode ? 54 : 72}
        outerRadius={printMode ? 88 : 112}
        paddingAngle={2}
        onClick={(entry) => {
          const label = typeof entry?.name === 'string' ? entry.name : null
          if (selectedDimension && label && onSelect) {
            onSelect(selectedDimension, label)
          }
        }}
      >
        {chartData.map((entry, index) => (
          <Cell key={`${entry.label}-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} cursor={onSelect ? 'pointer' : undefined} />
        ))}
      </Pie>
      {!printMode ? (
        <RechartsTooltip
          {...chartTooltipProps}
          formatter={(value, name) => [
            formatColumnValue(Number(value), activeMeasure, data.columnFormats),
            formatDimensionLabel(String(name ?? ''), selectedDimension, data.columnFormats),
          ]}
        />
      ) : null}
    </PieChart>
  )

  return (
    <div>
      {!printMode && availableDimensions.length > 1 ? (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs text-gray-500">Group by</span>
          <Select
            size="small"
            value={selectedDimension ?? undefined}
            onChange={(value) => setSelectedDimension(value)}
            options={availableDimensions.map((dimension) => ({ value: dimension, label: formatFieldDisplayName(dimension) }))}
            style={{ minWidth: 160 }}
          />
        </div>
      ) : null}

      {printMode ? (
        <div className="dashboard-print-chart-frame" style={{ height: PRINT_DONUT_HEIGHT }}>{chart}</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          {chart}
        </ResponsiveContainer>
      )}

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {chartData.map((entry, index) => (
          <button
            key={`${entry.label}-${index}-legend`}
            type="button"
            className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-left transition-colors hover:border-[var(--feature-border)] hover:bg-[var(--feature-surface)]"
            onClick={() => {
              if (selectedDimension && onSelect) {
                onSelect(selectedDimension, String(entry.label))
              }
            }}
          >
            <span className="flex items-center gap-2 text-sm text-gray-700">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }} />
              {formatDimensionLabel(entry.label, selectedDimension, data.columnFormats)}
            </span>
            <span className="font-medium text-gray-900">{formatColumnValue(entry.value, activeMeasure, data.columnFormats)}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
